import { createTheme, alpha } from "@mui/material/styles";

// App-wide design system: deep blue-green (teal) on white with a cool grey scale.
// Unifies colour, type, spacing, radii and component styling.
// Mirrors the CSS variables in index.css.

export const tokens = {
  primary: "#0D7377",
  primaryDark: "#0A5C60",
  primaryLight: "#14A3A8",
  primarySoft: "#E7F5F5",
  navy: "#0A2E31",
  // Neutral (slate) used where the app previously used a purple accent
  accent: "#475467",
  accentDark: "#344054",
  bg: "#F4F6FA",
  surface: "#FFFFFF",
  border: "#E4E7EC",
  borderStrong: "#D0D5DD",
  text: "#101828",
  textSecondary: "#667085",
  success: "#16A34A",
  warning: "#D97706",
  error: "#DC2626",
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  radiusSm: 8,
  radiusMd: 10,
  radiusLg: 12,
};

const softShadows = [
  "none",
  "0 1px 2px rgba(16,24,40,0.06)",
  "0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.04)",
  "0 4px 8px -2px rgba(16,24,40,0.08), 0 2px 4px -2px rgba(16,24,40,0.04)",
  "0 8px 16px -4px rgba(16,24,40,0.08), 0 4px 6px -2px rgba(16,24,40,0.03)",
];
// MUI needs 25 levels; reuse the strongest soft shadow above level 4.
const shadows = Array.from(
  { length: 25 },
  (_, i) => softShadows[Math.min(i, softShadows.length - 1)],
);
shadows[8] = "0 12px 24px -6px rgba(16,24,40,0.12)"; // menus / popovers
shadows[24] = "0 20px 40px -8px rgba(16,24,40,0.18)"; // dialogs

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.primary,
      dark: tokens.primaryDark,
      light: tokens.primaryLight,
    },
    secondary: { main: tokens.accent, dark: tokens.accentDark },
    success: { main: tokens.success },
    warning: { main: tokens.warning },
    error: { main: tokens.error },
    background: { default: tokens.bg, paper: tokens.surface },
    text: { primary: tokens.text, secondary: tokens.textSecondary },
    divider: tokens.border,
  },
  shape: { borderRadius: tokens.radiusSm },
  shadows,
  typography: {
    fontFamily: tokens.fontFamily,
    fontSize: 14,
    h1: { fontSize: "2rem", fontWeight: 700, lineHeight: 1.25 },
    h2: { fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.3 },
    h3: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3 },
    h4: { fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.35 },
    h5: { fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.4 },
    h6: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600 },
    subtitle2: { fontSize: "0.875rem", fontWeight: 600 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.75rem", lineHeight: 1.45 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  },
  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: "0 1px 0 rgba(16,24,40,0.08)" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusSm,
          fontWeight: 600,
          textTransform: "none",
          paddingInline: 16,
        },
        sizeSmall: { paddingInline: 12 },
        sizeLarge: { paddingBlock: 10, fontSize: "0.9375rem" },
        outlined: { borderColor: tokens.borderStrong },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: tokens.radiusSm } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusSm,
          backgroundColor: tokens.surface,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.borderStrong,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#B8BFCC",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1.5,
          },
          "&.Mui-disabled": { backgroundColor: "#F3F4F7" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: "0.875rem" } },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: tokens.radiusMd },
        outlined: { borderColor: tokens.border },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusMd,
          border: `1px solid ${tokens.border}`,
          boxShadow: softShadows[1],
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 16, "&:last-child": { paddingBottom: 16 } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 999 },
        sizeSmall: { fontSize: "0.75rem" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: tokens.radiusLg },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: "1.0625rem", fontWeight: 700, padding: "16px 20px" },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "16px 20px" } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: "12px 20px 16px", gap: 8 } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radiusSm,
          border: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "0.875rem", minHeight: 40 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radiusSm,
          border: `1px solid ${tokens.border}`,
          boxShadow: shadows[8],
        },
        option: { fontSize: "0.875rem", minHeight: 40 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: "#F7F8FA",
            color: tokens.textSecondary,
            fontWeight: 600,
            fontSize: "0.8125rem",
            borderBottom: `1px solid ${tokens.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.border}`,
          fontSize: "0.875rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover:hover": {
            backgroundColor: alpha(tokens.primary, 0.04),
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, minHeight: 44 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1F2937",
          fontSize: "0.75rem",
          borderRadius: 6,
          padding: "6px 10px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: tokens.radiusSm } },
    },
    MuiSnackbarContent: {
      styleOverrides: { root: { borderRadius: tokens.radiusSm } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRight: `1px solid ${tokens.border}` } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: tokens.radiusSm },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.border } },
    },
  },
});

export default theme;
